import React, { useState } from 'react';

interface LineChartProps {
  data: {
    label: string;
    value: number;
  }[];
  height?: number;
  showGrid?: boolean;
  color?: string;
  fillColor?: string;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 200,
  showGrid = true,
  color = '#003DA5',
  fillColor = '#003DA520'
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue;
  const padding = range * 0.1;
  
  const adjustedMax = maxValue + padding;
  const adjustedMin = minValue - padding;
  const adjustedRange = adjustedMax - adjustedMin;
  
  const width = 100;
  const chartHeight = height - 40; // Leave space for labels
  
  const points = data.map((item, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = chartHeight - ((item.value - adjustedMin) / adjustedRange) * chartHeight;
    return { x, y, value: item.value, label: item.label };
  });
  
  const pathData = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
  
  const areaPathData = `${pathData} L ${width} ${chartHeight} L 0 ${chartHeight} Z`;

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full"
        style={{ height: `${height}px` }}
      >
        {/* Grid lines */}
        {showGrid && (
          <g className="text-gray-200">
            {[0, 25, 50, 75, 100].map(percentage => {
              const y = (percentage / 100) * chartHeight;
              return (
                <line
                  key={percentage}
                  x1="0"
                  y1={y}
                  x2={width}
                  y2={y}
                  stroke="currentColor"
                  strokeDasharray="2 2"
                />
              );
            })}
          </g>
        )}
        
        {/* Area fill */}
        <path
          d={areaPathData}
          fill={fillColor}
          className="transition-opacity"
        />
        
        {/* Line */}
        <path
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth="2"
          className="transition-all"
        />
        
        {/* Data points */}
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === index ? 5 : 3}
              fill={color}
              className="transition-all cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            />
            {hoveredIndex === index && (
              <g>
                <rect
                  x={point.x - 30}
                  y={point.y - 35}
                  width="60"
                  height="25"
                  fill="white"
                  stroke={color}
                  strokeWidth="1"
                  rx="4"
                />
                <text
                  x={point.x}
                  y={point.y - 20}
                  textAnchor="middle"
                  className="text-xs font-medium fill-gray-900"
                >
                  ${point.value.toLocaleString()}
                </text>
              </g>
            )}
          </g>
        ))}
      </svg>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-gray-600">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0).map((item, index) => (
          <span key={index}>{item.label}</span>
        ))}
      </div>
    </div>
  );
};